import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Gift, MoveRight, Sparkles } from 'lucide-react-native'
import { calculateStampPreview, colors } from '@chillout/core'
import { MilestoneStampGrid } from '@chillout/ui'
import type { CardIconKey } from '@chillout/ui'

interface StampMilestone {
  stampPosition: number
  cardIcon: CardIconKey
}

interface StampPreviewProps {
  currentStamps: number
  stampDelta: number
  stampTarget: number
  completionCardIcon: CardIconKey
  milestones: StampMilestone[]
  title?: string
  stampSize?: number
  justify?: 'flex-start' | 'center'
}

interface SummaryItemProps {
  label: string
  value: number
  target: number
}

function SummaryItem({ label, value, target }: SummaryItemProps) {
  return (
    <View className="flex-row items-baseline gap-1">
      <Text className="text-lg web:lg:text-base font-bold text-primary">{label}:</Text>
      <Text className="text-lg web:lg:text-base font-bold text-accent">{value}</Text>
      <Text className="text-lg web:lg:text-base font-bold text-primary">/ {target}</Text>
    </View>
  )
}

export function StampPreview({
  currentStamps,
  stampDelta,
  stampTarget,
  completionCardIcon,
  milestones,
  title,
  stampSize = 44,
  justify = 'center',
}: StampPreviewProps) {
  const { t } = useTranslation()

  const isAdding = stampDelta > 0
  const afterRaw = currentStamps + stampDelta

  const previewCount = isAdding ? Math.min(stampDelta, stampTarget - currentStamps) : 0
  const removingCount = stampDelta < 0 ? Math.min(-stampDelta, currentStamps) : 0

  const { rewardsUnlocked, carryOver } = isAdding
    ? calculateStampPreview(currentStamps, stampDelta, stampTarget)
    : { rewardsUnlocked: 0, carryOver: 0 }

  const afterStamps = rewardsUnlocked > 0 ? carryOver : Math.max(0, afterRaw)

  const displayTitle = title ?? t('stampAwarding.stampPreview.label')

  return (
    <View className="gap-3">
      <Text className="text-sm font-semibold text-primary">
        {displayTitle}
      </Text>

      <MilestoneStampGrid
        current={currentStamps}
        total={stampTarget}
        completionCardIcon={completionCardIcon}
        milestones={milestones}
        previewCount={previewCount}
        removingCount={removingCount}
        stampSize={stampSize}
        gap={10}
        justify={justify}
        accessibilityLabels={{
          filled: t('stampAwarding.stampPreview.stampFilledAccessibilityLabel'),
          preview: t('stampAwarding.stampPreview.stampPreviewAccessibilityLabel'),
          empty: t('stampAwarding.stampPreview.stampEmptyAccessibilityLabel'),
        }}
      />

      <View className="mt-2 rounded-lg bg-canvas-primary px-5 py-4 web:lg:px-4 web:lg:py-3">
        <View className="flex-row items-center justify-center">
          <SummaryItem
            label={t('stampAwarding.stampPreview.currentLabel')}
            value={currentStamps}
            target={stampTarget}
          />
          <View className="px-3">
            <MoveRight size={18} color={colors.text.secondary} strokeWidth={2} />
          </View>
          {rewardsUnlocked > 0 ? (
            <View className="flex-row items-center gap-1">
              <Text className="text-lg web:lg:text-base font-bold text-primary">
                {t('stampAwarding.stampPreview.afterThisLabel')}:
              </Text>
              <Gift size={20} color={colors.accent.DEFAULT} strokeWidth={2} />
              <Text className="text-lg web:lg:text-base font-bold text-accent">x {rewardsUnlocked}</Text>
            </View>
          ) : (
            <SummaryItem
              label={t('stampAwarding.stampPreview.afterThisLabel')}
              value={afterStamps}
              target={stampTarget}
            />
          )}
        </View>
      </View>
      {rewardsUnlocked > 0 ? (
        <View className="flex-row items-center justify-center gap-2">
          <Sparkles size={18} color={colors.accent.DEFAULT} strokeWidth={1.5} />
          <View className="flex-row items-baseline gap-1">
            <Text className="text-base font-bold text-primary">
              {t('stampAwarding.stampPreview.nextCardStartsLabel')}
            </Text>
            <Text className="text-base font-bold text-accent">{carryOver}</Text>
            <Text className="text-base font-bold text-primary">/ {stampTarget}</Text>
          </View>
        </View>
      ) : null}
    </View>
  )
}